import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/GlobalAPI';

const E = [0.16, 1, 0.3, 1];

const col = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: E } },
};

const BlogContent = () => {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(API_ENDPOINTS.BLOGS)
      .then(res => {
        const data = res.data || [];
        const feat = data.find(d => d.type === 'Featured') || data[0];
        const others = data.filter(d => d._id !== feat?._id);
        setFeatured(feat || null);
        setPosts(others);
      })
      .catch(err => console.error('Error loading posts:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-[#FAFBF9] gap-4">
        <div className="w-8 h-8 rounded-full border-2 border-[#1C2E1E]/20 border-t-[#1C2E1E] animate-spin" />
        <span className="text-[#5A635A] text-sm font-medium">Loading stories...</span>
      </div>
    );
  }

  if (!featured && posts.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#FAFBF9]">
        <p className="text-[#5A635A] font-light">No posts found.</p>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-[#FAFBF9] text-[#1D1D1F] font-sans selection:bg-[#EAECE9] selection:text-[#1C2E1E] antialiased overflow-x-hidden pt-12">
      
      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[600px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 80% 20%, rgba(112,0,255,0.04) 0%, transparent 60%)' }} />

      {/* ── HERO HEADER SECTION ── */}
      <section className="relative overflow-hidden pt-16 sm:pt-24 lg:pt-28 pb-16 sm:pb-24 lg:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 text-left">
          <motion.div
            initial="hidden"
            animate="show"
            variants={col}
            className="max-w-3xl"
          >
            <motion.span variants={fadeUp} className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-8">
              ✦&nbsp;&nbsp;Stories &amp; Insights&nbsp;&nbsp;✦
            </motion.span>
            <motion.h1 variants={fadeUp} className="text-[44px] sm:text-5xl md:text-6xl font-extralight text-[#1D1D1F]/30 leading-[1.05] tracking-tight">
              Insights &amp;
            </motion.h1>
            <motion.h1 variants={fadeUp} className="text-[44px] sm:text-5xl md:text-6xl font-black text-[#1D1D1F] leading-[1.05] tracking-tight mb-8">
              Stories.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-[#5A635A] font-light leading-[1.75] max-w-[48ch]">
              Discover the latest trends, strategies, and growth perspectives in digital transformation, conversion optimization, and design engineering.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── FEATURED BLOG SECTION ── */}
      {featured && (
        <section className="relative bg-white border-t border-[#F1F3F1] py-20 sm:py-28">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
            <div className="grid lg:grid-cols-[50%,1fr] gap-12 lg:gap-16 items-center">
              
              {/* Text */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, ease: E }}
                className="space-y-6"
              >
                <div>
                  <span className="inline-flex px-3.5 py-1.5 border border-pink-200 rounded-full text-[9px] font-bold uppercase tracking-wider text-[#FF1E90] bg-pink-50/50">
                    FEATURED STORY
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-[44px] font-bold text-black tracking-tight leading-tight">
                  {featured.heading}
                </h2>
                <p className="text-base text-[#5A635A] font-light leading-[1.75] max-w-prose">
                  {featured.subtext}
                </p>
                <div>
                  <button
                    onClick={() => navigate(`/blog/${featured._id}`)}
                    className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-[#1C2E1E] hover:opacity-80 transition-colors group"
                  >
                    Read the full story
                    <FaArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>

              {/* Image Tile */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, ease: E }}
              >
                {featured.image && (
                  <div 
                    onClick={() => navigate(`/blog/${featured._id}`)}
                    className="relative overflow-hidden rounded-[28px] w-full cursor-pointer group"
                    style={{ boxShadow: '0 32px 64px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)' }}
                  >
                    <img
                      src={featured.image}
                      alt={featured.heading}
                      className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-350 aspect-[16/11]"
                      loading="eager"
                    />
                  </div>
                )}
              </motion.div>

            </div>
          </div>
        </section>
      )}

      {/* ── OTHER POSTS GRID SECTION ── */}
      {posts.length > 0 && (
        <section className="relative bg-[#FAFBF9] border-t border-[#F1F3F1] py-24 sm:py-36">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
            
            {/* Sub-heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-4">
                ✦&nbsp;&nbsp;Archive&nbsp;&nbsp;✦
              </span>
              <h3 className="text-3xl sm:text-[40px] font-extralight text-[#1D1D1F]/30 leading-tight tracking-tight">
                More
              </h3>
              <h3 className="text-3xl sm:text-[40px] font-bold text-[#1D1D1F] leading-tight tracking-tight -mt-1">
                Stories.
              </h3>
            </motion.div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {posts.map((post, i) => (
                <motion.div
                  key={post._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.2), ease: 'easeOut' }}
                >
                  <div 
                    onClick={() => navigate(`/blog/${post._id}`)}
                    className="bg-white border border-[#EAECE9] rounded-[28px] p-6 relative overflow-hidden flex flex-col justify-between group hover:border-[#1C2E1E] transition-all duration-350 min-h-[380px] cursor-pointer shadow-sm hover:shadow-md"
                  >
                    <div>
                      {/* Image header */}
                      {post.image && (
                        <div className="h-44 w-full overflow-hidden rounded-[20px] mb-5 border border-[#F1F3F1]">
                          <img
                            src={post.image}
                            alt={post.heading}
                            className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-350"
                            loading="lazy"
                          />
                        </div>
                      )}

                      {/* Badge if featured */}
                      {post.type === 'Featured' && (
                        <span className="inline-flex px-2 py-0.5 border border-pink-100 rounded-full text-[8px] font-bold uppercase tracking-wider text-[#FF1E90] bg-pink-50 mb-3">
                          Featured
                        </span>
                      )}

                      {/* Title */}
                      <h4 className="text-base font-bold text-black mb-2 tracking-tight group-hover:bg-gradient-to-r group-hover:from-[#FF7332] group-hover:via-[#FF1E90] group-hover:to-[#7000FF] group-hover:bg-clip-text group-hover:text-transparent transition-all">
                        {post.heading}
                      </h4>

                      {/* Subtext */}
                      <p className="text-[#5A635A] text-xs leading-relaxed mb-6 font-light line-clamp-3">
                        {post.subtext}
                      </p>
                    </div>

                    {/* Bottom CTA link */}
                    <div className="flex items-center gap-2 pt-4 border-t border-[#F1F3F1] text-[11px] font-semibold uppercase tracking-wider text-[#1C2E1E]/40 group-hover:text-[#1C2E1E] transition-colors duration-250">
                      <span>Read Story</span>
                      <FaArrowRight size={9} className="group-hover:translate-x-1 transition-transform" />
                    </div>

                  </div>
                </motion.div>
              ))}
            </div>

          </div>
        </section>
      )}

    </div>
  );
};

export default BlogContent;
